import { AgreementListRequest, AgreementShape } from '../types'
import { useAgreements } from '../queries'
import { DataGridPro, ExtendedDataGridProProps } from '@cibo/ui'
import { Skeleton, Typography } from '@mui/material'
import { GridRowParams } from '@mui/x-data-grid-pro'
import { ADMIN_ROUTES } from 'navigation/types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate, useParams } from 'react-router-dom'

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50]

export enum AgreementsGridColumns {
  STATUS = 'status',
  YEAR = 'year',
  USER_ID = 'userId',
  REVISIONS = 'revisions',
  LAST_COMMENT = 'lastComment',
  MODIFIED_DATE = 'modifiedDate',
}
interface PaginatedAgreementsTableProps extends Pick<ExtendedDataGridProProps, 'columns'> {
  baseQuery?: Omit<AgreementListRequest, 'limit' | 'offset' | 'sort'>
  includeStats?: boolean
}

export const PaginatedAgreementsTable = ({
  baseQuery,
  includeStats,
  columns,
}: PaginatedAgreementsTableProps) => {
  const { t } = useTranslation('@cibo/impact/PaginatedAgreementsTable')
  const [page, setPage] = useState(0)
  const { type } = useParams()
  const [pageSize, setPageSize] = useState(ROWS_PER_PAGE_OPTIONS[0])
  const agreements = useAgreements(
    {
      offset: page * pageSize,
      limit: pageSize,
      ...baseQuery,
    },
    includeStats
  )

  const navigate = useNavigate()

  const handleClickRow = ({ row: { agreementId, userId } }: GridRowParams<AgreementShape>) => {
    navigate(generatePath(ADMIN_ROUTES.AGREEMENT_DETAIL, { agreementId, type }))
  }

  if (!agreements.isFetched) {
    return <Skeleton variant="rounded" height={100} />
  }

  if (agreements.data?.numAvailable === 0) {
    return (
      <Typography>
        {t('empty', {
          context: baseQuery?.status,
          agreementType: t('agreementType', { context: baseQuery?.agreementType }),
        })}
      </Typography>
    )
  }

  return (
    <DataGridPro
      columns={columns}
      density="compact"
      getRowId={({ agreementId }) => agreementId}
      headerPaletteColor="primary"
      loading={agreements.isLoading}
      onRowClick={handleClickRow}
      onPageChange={setPage}
      onPageSizeChange={setPageSize}
      pageSize={pageSize}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      rows={agreements.data?.items || []}
      rowCount={agreements.data?.numAvailable}
      paginationMode="server"
      autoHeight
      disableSelectionOnClick
      disableColumnFilter
      disableColumnMenu
      disableColumnReorder
      disableColumnPinning
      pagination
    />
  )
}
